import { useState } from "react";
import { STATUS_STYLES, ASSET_TYPES, BUCKET_STYLES } from "./constants";

const BUCKET_ABBR = { formal_hta: "HTA", cost_minimisation: "CM", hospital_agreement: "HP" };

export const Heatmap = ({ data }) => {
  const [selected, setSelected] = useState(null);
  if (!data) return null;
  const { assets, countries, matrix, bucket_summary } = data;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-5">
        {Object.entries(STATUS_STYLES).map(([k, s]) => (
          <span key={k} className="flex items-center gap-2 text-xs text-slate-600">
            <span className={`h-3 w-3 ${s.bg}`} /> {s.label}
          </span>
        ))}
      </div>
      <div className="overflow-x-auto border border-slate-200 bg-white">
        <table data-testid="reusability-heatmap" className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left p-4 min-w-[260px] sticky left-0 bg-white z-10">
                <span className="overline-label text-slate-400">Evidence Asset × Market</span>
              </th>
              {countries.map((c) => (
                <th key={c.id} className="p-2 text-center min-w-[64px]">
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-lg">{c.flag}</span>
                    <span className="font-mono-data text-[10px] text-slate-500">{c.code}</span>
                    <span className="h-1 w-6" style={{ background: BUCKET_STYLES[c.bucket].color }} />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {assets.map((a) => (
              <tr key={a.id} className="border-b border-slate-100 last:border-0">
                <td className="p-4 sticky left-0 bg-white z-10 align-top">
                  <p className="font-semibold text-slate-900 leading-snug">{a.name}</p>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 border border-slate-200 px-1.5 py-0.5">{ASSET_TYPES[a.type] || a.type}</span>
                    {bucket_summary[a.id] &&
                      Object.entries(bucket_summary[a.id]).map(([bid, s]) => (
                        <span key={bid} className="flex items-center gap-1 text-[10px] text-slate-500" title={`${bid}: ${STATUS_STYLES[s.dominant].label}`}>
                          {BUCKET_ABBR[bid]}
                          <span className={`h-2 w-2 ${STATUS_STYLES[s.dominant].bg}`} />
                        </span>
                      ))}
                  </div>
                </td>
                {countries.map((c) => {
                  const cell = matrix[a.id]?.[c.id];
                  const isSel = selected && selected.asset === a.id && selected.country === c.id;
                  return (
                    <td key={c.id} className="p-1.5 text-center">
                      <button
                        data-testid={`heatmap-cell-${a.id}-${c.id}`}
                        title={cell?.rationale}
                        onClick={() => setSelected({ asset: a.id, country: c.id, assetName: a.name, countryName: c.name, flag: c.flag, ...cell })}
                        className={`h-9 w-9 mx-auto block ${STATUS_STYLES[cell?.status]?.bg || "bg-slate-100"} ${isSel ? "ring-2 ring-slate-900 ring-offset-2" : "hover:opacity-80"}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selected && (
        <div data-testid="heatmap-cell-detail" className="border border-slate-200 bg-white p-5 flex gap-4 items-start stagger-in">
          <span className={`h-4 w-4 mt-1 shrink-0 ${STATUS_STYLES[selected.status].bg}`} />
          <div>
            <p className="text-sm font-semibold text-slate-900">
              {selected.assetName} · {selected.flag} {selected.countryName} — <span className={`px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${STATUS_STYLES[selected.status].chip}`}>{STATUS_STYLES[selected.status].label}</span>
            </p>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">{selected.rationale}</p>
          </div>
        </div>
      )}
    </div>
  );
};
